import React from "react";
import { format2 } from "../helpers/helper";
import { Box, Typography, useTheme } from "@mui/material";

export const Donut = ({ 
    project = 0, 
    retail = 0, 
    label, 
    isMobile, 
    colors,
    hoveredCategory,
    setHoveredCategory    
}) => {
    const theme = useTheme();
    const SIZE = isMobile ? 140 : 150;
    const CENTER = SIZE / 2;
    const RADIUS = isMobile ? 50 : 60;
    const STROKE = isMobile ? 14 : 18;
    const GAP_ANGLE = 1;
    const SAFE_RADIUS = RADIUS - STROKE / 2;
    const total = project + retail;
    const hasValue = total > 0;
    const retailColor = colors?.retail || "#07B8B2";
    const projectColor = colors?.project || "#525EB4"; 
    const baseColor = theme.palette.divider;
    const retailOpacity = hoveredCategory && hoveredCategory !== 'retail' ? 0.3 : 1;
    const projectOpacity = hoveredCategory && hoveredCategory !== 'project' ? 0.3 : 1;
    const retailPercent = hasValue ? Math.round((retail / total) * 100) : 0;
    const projectPercent = hasValue ? Math.round((project / total) * 100) : 0;
    let centerText = format2(total);
    let centerSuffix = "";
    let centerColor = "text.primary";

    if (hoveredCategory === 'retail') {
        centerText = retailPercent;
        centerSuffix = "%";
        centerColor = retailColor;
    } else if (hoveredCategory === 'project') {
        centerText = projectPercent;
        centerSuffix = "%";
        centerColor = projectColor;
    }

    const polarToCartesian = (cx, cy, r, angle) => {
        const rad = ((angle - 90) * Math.PI) / 180;
        return {
            x: cx + r * Math.cos(rad),
            y: cy + r * Math.sin(rad),
        };
    };

    const describeArc = (cx, cy, r, start, end) => {
        const startPoint = polarToCartesian(cx, cy, r, end);
        const endPoint = polarToCartesian(cx, cy, r, start);
        const largeArcFlag = end - start <= 180 ? 0 : 1;
        return `M ${startPoint.x} ${startPoint.y}
            A ${r} ${r} 0 ${largeArcFlag} 0 ${endPoint.x} ${endPoint.y}`;
    };

    const retailSweep = hasValue
        ? Math.max((retail / total) * 360 - GAP_ANGLE, 4)
        : 0;

    const projectSweep = hasValue
        ? Math.max((project / total) * 360 - GAP_ANGLE, 4)
        : 0;

    const startRetail = GAP_ANGLE / 2;
    const endRetail = startRetail + retailSweep;
    const startProject = endRetail + GAP_ANGLE;

    return (
        <Box
            sx={{
                width: SIZE,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <Typography
                sx={{
                    fontSize: isMobile ? 11 : 12,
                    color: retailColor,
                    transform: isMobile ? "translateX(-25%)" : "translateX(50%)",
                    opacity: retailOpacity,
                    transition: "opacity 0.3s ease",
                    fontWeight: hoveredCategory === 'retail' ? 700 : 400
                }}
            >
                {format2(retail)}
            </Typography>

            <Box sx={{ width: SIZE, height: SIZE, position: "relative" }}>
                <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
                    <circle
                        cx={CENTER}
                        cy={CENTER}
                        r={SAFE_RADIUS}
                        fill="none"
                        stroke={baseColor}
                        strokeWidth={STROKE}
                    />

                    {hasValue && retail > 0 && (
                        <path
                            d={describeArc(CENTER, CENTER, SAFE_RADIUS, startRetail, endRetail)}
                            fill="none"
                            stroke={retailColor}
                            strokeWidth={STROKE}
                            style={{ 
                                opacity: retailOpacity, 
                                transition: "opacity 0.3s ease",
                                cursor: "pointer" 
                            }}
                            onMouseEnter={() => setHoveredCategory('retail')}
                            onMouseLeave={() => setHoveredCategory(null)}
                        />
                    )}

                    {hasValue && project > 0 && (
                        <path
                            d={describeArc(CENTER, CENTER, SAFE_RADIUS, startProject, startProject + projectSweep)}
                            fill="none"
                            stroke={projectColor}
                            strokeWidth={STROKE}
                            style={{ 
                                opacity: projectOpacity, 
                                transition: "opacity 0.3s ease",
                                cursor: "pointer" 
                            }}
                            onMouseEnter={() => setHoveredCategory('project')}
                            onMouseLeave={() => setHoveredCategory(null)}
                        />
                    )}
                </svg>

                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        textAlign: "center",
                        pointerEvents: "none"
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: isMobile ? 11 : 12,
                            color: centerColor,
                            fontWeight: 600,
                            transition: "color 0.2s ease",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {centerText}
                        {centerSuffix && <span style={{ fontSize: '0.9em', marginLeft: 1 }}>{centerSuffix}</span>}
                    </Typography>
                </Box>
            </Box>

            <Typography
                sx={{
                    fontSize: isMobile ? 11 : 12,
                    color: projectColor,
                    transform: isMobile ? "translateX(25%)" : "translateX(-50%)",
                    opacity: projectOpacity,
                    transition: "opacity 0.3s ease",
                    fontWeight: hoveredCategory === 'project' ? 700 : 400
                }}
            >
                {format2(project)}
            </Typography>

            <Typography
                sx={{
                    mt: 1,
                    fontSize: isMobile ? 12 : 14,
                    color: "text.secondary",
                }}
            >
                {label}
            </Typography>
        </Box>
    );
};

export const Legend = ({ color, label, categoryKey, hoveredCategory, setHoveredCategory }) => {
    const opacity = hoveredCategory && hoveredCategory !== categoryKey ? 0.3 : 1;

    return (
        <Box 
            sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 1, 
                opacity, 
                transition: "opacity 0.3s ease",
                cursor: "pointer"
            }}
            onMouseEnter={() => setHoveredCategory(categoryKey)}
            onMouseLeave={() => setHoveredCategory(null)}
        >
            <Box sx={{ width: 2, height: 16, bgcolor: color }} />
            <Typography sx={{ fontSize: 12, color, fontWeight: hoveredCategory === categoryKey ? 700 : 400 }}>
                {label}
            </Typography>
        </Box>
    );
};